import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, query, where, orderBy, getDocs, updateDoc, arrayUnion, arrayRemove, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import MovieItem from '../components/MovieItem';
import { FaExpand, FaCompress, FaShare, FaUser } from 'react-icons/fa';
import { GoHeartFill } from "react-icons/go";
import { BiSolidMessageSquareDetail } from "react-icons/bi";
import { IoBookmark } from "react-icons/io5";
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';
import CommentSection from '../components/CommentSection';
import Modal from 'react-modal';

// Set the app element for accessibility (required by react-modal)
Modal.setAppElement('#root');

// Define the styles for the MovieView page
const styles = `
    /* Main container */
    .movie-view-container {
        min-height: 100vh;
        background: #000;
        color: #fff;
    }
    
    /* Video player container */
    .video-player-wrapper {
        background: #000;
        display: flex;
        justify-content: center;
        width: 100%;
        overflow: hidden;
    }
    
    .video-player-container {
        background: #000;
        position: relative;
        width: 100%;
    }
    
    /* Desktop specific video sizing */
    @media (min-width: 768px) {
        .video-player-container {
            width: 853px;
            height: 480px;
            margin: 20px auto;
        }
    }
    
    /* Mobile video sizing */
    @media (max-width: 767px) {
        .video-player-container {
            width: 100%;
            padding-bottom: 56.25%; /* 16:9 aspect ratio */
        }
    }
    
    .video-player-container.fullscreen {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1000;
        margin: 0;
    }
    
    .video-player-container iframe {
        position: absolute;
        top: -60px;
        left: 0;
        width: 100%;
        height: calc(100% + 60px);
        border: none;
    }
    
    /* Fullscreen toggle button */
    .fullscreen-toggle {
        position: absolute;
        bottom: 10px;
        right: 10px;
        background: rgba(0, 0, 0, 0.7);
        color: #fff;
        border: none;
        border-radius: 50%;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        z-index: 20;
    }
    
    /* Content below video */
    .content-below-video {
        max-width: 853px;
        margin: 0 auto;
        padding: 0 16px;
    }
    
    /* Live Viewers Row */
    .live-viewers-row {
        background: #1a1a1a;
        padding: 0.5rem 0.75rem;
        border-bottom: 1px solid #333;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .viewer-profile {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 2px solid #fff;
        object-fit: cover;
    }

    .viewer-profile:not(:first-child) {
        margin-left: -10px;
    }

    
    /* Actor info section */
    .actor-info {
        background: #1a1a1a;
        padding: 0.75rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid #333;
    }
    
    .actor-profile {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }
    
    .actor-profile img {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        object-fit: cover;
    }
    
    .actor-details {
        display: flex;
        flex-direction: column;
    }
    
    .actor-name {
        font-size: 1rem;
        font-weight: 600;
        color: #fff;
    }
    
    .release-year {
        font-size: 0.75rem;
        color: #aaa;
    }
    
    .overview-btn {
        background: #FF1493;
        color: #fff;
        border: none;
        border-radius: 9999px;
        padding: 0.4rem 1rem;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.3s ease;
    }
    
    .overview-btn:hover {
        background: #e01385;
    }
    
    /* Action buttons row - Modified for mobile */
    .action-buttons {
        width: 100%;
        background: #1f2937;
        padding: 0.5rem;
        margin: 0.75rem 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-radius: 0.375rem;
    }
    
    .action-button {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.25rem;
        color: #fff;
        cursor: pointer;
        font-size: 0.75rem;
        transition: color 0.2s ease;
        padding: 0.25rem;
        min-width: 60px;
    }
    
    .action-button:hover {
        opacity: 0.8;
    }
    
    .action-button.liked {
        color: #FF1493;
    }
    
    .action-button.saved {
        color: #1FA9FF;
    }

    @keyframes likeBounce {
        0%, 100% { transform: scale(1); }
        25% { transform: scale(1.2); }
        50% { transform: scale(0.9); }
        75% { transform: scale(1.1); }
    }

    @keyframes saveBounce {
        0%, 100% { transform: scale(1); }
        25% { transform: scale(1.2) rotate(-10deg); }
        50% { transform: scale(0.9); }
        75% { transform: scale(1.1) rotate(10deg); }
    }
    
    /* Recommended movies section - Modified for mobile */
    .recommended-section {
        padding: 1rem 0;
    }
    
    .recommended-title {
        font-size: 1.25rem;
        font-weight: 600;
        color: #666;
        margin-bottom: 1rem;
        padding: 0 0.5rem;
    }
    
    .recommended-movies-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.5rem;
        padding: 0 0.25rem;
    }
    
    @media (min-width: 640px) {
        .recommended-movies-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 0.75rem;
        }
    }
    
    @media (min-width: 768px) {
        .recommended-movies-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 0.75rem;
        }
    }
    
    @media (min-width: 1024px) {
        .recommended-movies-grid {
            grid-template-columns: repeat(5, 1fr);
            gap: 1rem;
        }

        .action-button {
            flex-direction: row;
            min-width: auto;
        }
    }
    
    .movie-item-wrapper {
        position: relative;
        transition: transform 0.3s ease;
    }
    
    .movie-item-wrapper:hover {
        transform: scale(1.05);
    }
    
    .no-recommendations {
        text-align: center;
        color: #666;
        padding: 2rem 0;
        grid-column: 1 / -1;
    }
`;

// Inject the styles into the document
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default function MovieView() {
    const { movieId } = useParams();
    const navigate = useNavigate();
    const auth = getAuth();
    const [movie, setMovie] = useState(null);
    const [recommendedMovies, setRecommendedMovies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);
    const [isLiked, setIsLiked] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [likeCount, setLikeCount] = useState(0);
    const [commentCount, setCommentCount] = useState(0);
    const [liveViewers, setLiveViewers] = useState(0);
    const [recentViewers, setRecentViewers] = useState([]);
    const [likeAnimation, setLikeAnimation] = useState(false);
    const [saveAnimation, setSaveAnimation] = useState(false);
    const [showComments, setShowComments] = useState(false);

    useEffect(() => {
        if (!auth.currentUser || !movieId) return;

        const movieRef = doc(db, 'movies', movieId);
        const userId = auth.currentUser.uid;
        const profileImage = auth.currentUser.photoURL || defaultAvatar;

        const addViewer = async () => {
            try {
                await updateDoc(movieRef, {
                    currentViewers: liveViewers + 1,
                    recentViewers: arrayUnion({ userId, profileImage })
                });
            } catch (error) {
                console.error("Error adding viewer:", error);
            }
        };

        addViewer();

        const unsubscribe = onSnapshot(movieRef, (doc) => {
            if (doc.exists()) {
                const data = doc.data();
                setLiveViewers(data.currentViewers || 0);
                setRecentViewers(data.recentViewers || []);
            }
        });

        return () => {
            const removeViewer = async () => {
                try {
                    await updateDoc(movieRef, {
                        currentViewers: liveViewers > 0 ? liveViewers - 1 : 0,
                        recentViewers: arrayRemove({ userId, profileImage })
                    });
                } catch (error) {
                    console.error("Error removing viewer:", error);
                }
            };
            removeViewer();
            unsubscribe();
        };
    }, [auth.currentUser, movieId, liveViewers]);

    useEffect(() => {
        if (!movieId) return;

        const commentsRef = collection(db, `movies/${movieId}/comments`);
        const q = query(commentsRef, orderBy('timestamp', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setCommentCount(snapshot.size);
        }, (error) => {
            console.error("Error fetching comment count:", error);
        });

        return () => unsubscribe();
    }, [movieId]);

    const getValidYouTubeUrl = (url) => {
        if (!url) return null;
        
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        
        if (match && match[2].length === 11) {
            return `https://www.youtube.com/embed/${match[2]}?autoplay=1&showinfo=0&rel=0&controls=1&modestbranding=1&disablekb=1&fs=0`;
        }
        
        try {
            const urlObj = new URL(url);
            if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
                const videoId = urlObj.searchParams.get('v') || urlObj.pathname.split('/').pop();
                if (videoId && videoId.length === 11) {
                    return `https://www.youtube.com/embed/${videoId}?autoplay=1&showinfo=0&rel=0&controls=1&modestbranding=1&disablekb=1&fs=0`;
                }
            }
        } catch (e) {
            console.error("Invalid YouTube URL:", url);
        }
        
        return null;
    };

    const handleLike = async () => {
        if (!auth.currentUser) {
            toast.error("Please sign in to like movies");
            return;
        }

        const movieRef = doc(db, 'movies', movieId);
        const userId = auth.currentUser.uid;

        try {
            setLikeAnimation(true);
            setTimeout(() => setLikeAnimation(false), 1000);
            
            if (isLiked) {
                await updateDoc(movieRef, {
                    likedBy: arrayRemove(userId),
                    likes: likeCount - 1
                });
                setLikeCount(likeCount - 1);
                setIsLiked(false);
            } else {
                await updateDoc(movieRef, {
                    likedBy: arrayUnion(userId),
                    likes: likeCount + 1
                });
                setLikeCount(likeCount + 1);
                setIsLiked(true);
            }
        } catch (error) {
            toast.error("Error updating like status");
            console.error("Like error:", error);
        }
    };

    const handleSave = async () => {
        if (!auth.currentUser) {
            toast.error("Please sign in to save movies");
            return;
        }

        const movieRef = doc(db, 'movies', movieId);
        const userId = auth.currentUser.uid;

        try {
            setSaveAnimation(true);
            setTimeout(() => setSaveAnimation(false), 1000);
            
            if (isSaved) {
                await updateDoc(movieRef, {
                    savedBy: arrayRemove(userId)
                });
                setIsSaved(false);
            } else {
                await updateDoc(movieRef, {
                    savedBy: arrayUnion(userId)
                });
                setIsSaved(true);
            }
        } catch (error) {
            toast.error("Error updating save status");
            console.error("Save error:", error);
        }
    };

    const handleShare = async () => {
        const shareUrl = `${window.location.origin}/movie/${movieId}`;
        const shareData = {
            title: movie.title,
            text: `Check out "${movie.title}" on AweitFlix! Starring ${movie.mainActorName || "an amazing cast"}.`,
            url: shareUrl,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(shareUrl);
                toast.info('Movie link copied to clipboard! Share it manually.');
            }
        } catch (error) {
            console.error('Error sharing movie:', error);
            try {
                await navigator.clipboard.writeText(shareUrl);
                toast.info('Movie link copied to clipboard! Share it manually.');
            } catch (clipboardError) {
                console.error('Error copying to clipboard:', clipboardError);
                toast.error('Failed to share movie. Please share the link manually.');
            }
        }
    };

    const handleComment = () => {
        if (!auth.currentUser) {
            toast.error("Please sign in to comment");
            return;
        }
        setShowComments(!showComments);
    };
    useEffect(() => {
    if (!movieId || !auth.currentUser) return;

    const movieDocRef = doc(db, 'movies', movieId);

    const unsubscribe = onSnapshot(movieDocRef, async (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            setMovie({ id: docSnap.id, ...data });

            setIsLiked(data.likedBy?.includes(auth.currentUser.uid) || false);
            setIsSaved(data.savedBy?.includes(auth.currentUser.uid) || false);
            setLikeCount(data.likes || 0);

            const validUrl = getValidYouTubeUrl(data.youtubeUrl);
            setVideoUrl(validUrl);

            const recommendedQuery = query(
                collection(db, 'movies'),
                where('genre', '==', data.genre),
                orderBy('timestamp', 'desc')
            );
            const recommendedSnap = await getDocs(recommendedQuery);
            const recommendedList = recommendedSnap.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(m => m.id !== movieId);
            setRecommendedMovies(recommendedList);
        } else {
            toast.error("Movie not found");
            navigate('/');
        }
        setLoading(false);
    }, (error) => {
        toast.error("Failed to load movie");
        console.error("onSnapshot error:", error);
        setLoading(false);
    });

    return () => unsubscribe();
}, [movieId, auth.currentUser, navigate]);


  const toggleFullscreen = () => {
    const iframeContainer = document.querySelector('.video-player-container');

    if (!document.fullscreenElement) {
        if (iframeContainer.requestFullscreen) {
            iframeContainer.requestFullscreen();
        } else if (iframeContainer.webkitRequestFullscreen) { // Safari
            iframeContainer.webkitRequestFullscreen();
        } else if (iframeContainer.msRequestFullscreen) { // IE11
            iframeContainer.msRequestFullscreen();
        }
    } else {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
    }
};



    if (loading || !movie) {
        return <Spinner />;
    }

    return (
        <div className="movie-view-container">
            <div className="video-player-wrapper">
                <div className={`video-player-container ${isFullscreen ? 'fullscreen' : ''}`}>
                    {videoUrl ? (
                        <iframe
                            src={videoUrl}
                            title={movie.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            frameBorder="0"
                        ></iframe>
                    ) : (
                        <div className="flex items-center justify-center h-full text-gray-400">
                            <p>Something went wrong! Check your internet connection.</p>
                        </div>
                    )}
                    <button onClick={toggleFullscreen} className="fullscreen-toggle">
                        {isFullscreen ? <FaCompress /> : <FaExpand />}
                    </button>
                </div>
            </div>

            <div className="content-below-video">
                <div className="live-viewers-row">
                    <div className="bg-gray-900 rounded-full px-3 py-1 flex items-center space-x-2">
                        <FaUser className="text-white text-sm" />
                        <span className="text-white text-sm font-medium">
                            {liveViewers >= 1000 ? `${(liveViewers / 1000).toFixed(1)}K` : liveViewers}
                        </span>
                        {recentViewers.slice(0, 5).map((viewer, index) => (
                            <img
                                key={index}
                                src={viewer.profileImage || defaultAvatar}
                                alt="Viewer"
                                className="viewer-profile"
                            />
                        ))}
                    </div>
                </div>

               <h2 className="text-xl font-bold text-white py-4 px-3 bg-gray-900 border-b border-gray-700">
  {movie.title}
</h2>
                
                <div className="actor-info">
                    <div className="actor-profile">
                        <img
                            src={movie.actorImageUrl || defaultAvatar}
                            alt={movie.mainActorName || 'Main Actor'}
                        />
                        <div className="actor-details">
                            <span className="actor-name">{movie.mainActorName || 'Unknown Actor'}</span>
                            <span className="release-year">{movie.releaseYear || 'Unknown Year'}</span>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate(`/movie/${movieId}/about`)}
                        className="overview-btn"
                    >
                        Overview
                    </button>
                </div>

               <div className="action-buttons">
                    <button 
                        className={`action-button ${isLiked ? 'liked' : ''}`}
                        onClick={handleLike}
                    >
                        <GoHeartFill 
                            size={22} 
                            className={likeAnimation ? "animate-[likeBounce_1s_ease-in-out]" : ""} 
                        />
                        <span>{likeCount}</span>
                    </button>
                    <button 
                        className={`action-button ${showComments ? 'text-pink-500' : ''}`}
                        onClick={handleComment}
                    >
                        <BiSolidMessageSquareDetail size={22} />
                        <span>{commentCount}</span>
                    </button>
                    <button 
                        className={`action-button ${isSaved ? 'saved' : ''}`}
                        onClick={handleSave}
                    >
                        <IoBookmark 
                            size={22} 
                            className={saveAnimation ? "animate-[saveBounce_1s_ease-in-out]" : ""} 
                        />
                        <span>{isSaved ? 'Saved' : 'Save'}</span>
                    </button>
                    <button 
                        className="action-button"
                        onClick={handleShare}
                    >
                        <FaShare size={22} />
                        <span>Share</span>
                    </button>
                </div>

                <Modal
                    isOpen={showComments}
                    onRequestClose={() => setShowComments(false)}
                    style={{
                        content: {
                            top: '50%',
                            left: '50%',
                            right: 'auto',
                            bottom: 'auto',
                            marginRight: '-50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: '#1a1a1a',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '20px',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            width: '90%',
                            maxWidth: '500px',
                        },
                        overlay: {
                            backgroundColor: 'rgba(0, 0, 0, 0.75)',
                            zIndex: 1000,
                        },
                    }}
                    contentLabel="Comment Modal"
                >
                    <CommentSection contentId={movieId} contentType="movie" />
                    <button
                        onClick={() => setShowComments(false)}
                        className="mt-4 px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
                    >
                        Close
                    </button>
                </Modal>

                <div className="recommended-section">
                    <h2 className="recommended-title">Recommended for you</h2>
                    {recommendedMovies.length > 0 ? (
                        <div className="recommended-movies-grid">
                            {recommendedMovies.map(recommendedMovie => (
                                <div key={recommendedMovie.id} className="movie-item-wrapper">
                                    <MovieItem
                                        movie={recommendedMovie}
                                        onClick={() => navigate(`/movie/${recommendedMovie.id}`)}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="no-recommendations">
                            No recommended movies available.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}